import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { formatMoney, formatDate, todayISO } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowDownRight, ArrowUpRight, Plus, Pencil, Trash2, Paperclip, Filter } from "lucide-react";
import { toast } from "sonner";

const emptyForm = {
  type: "income",
  amount: "",
  currency: "TRY",
  date: todayISO(),
  category_id: "",
  bank_account_id: "",
  contact_id: "",
  description: "",
  attachment_ids: [],
};

export default function Transactions() {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [banks, setBanks] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [uploading, setUploading] = useState(false);
  const [attachments, setAttachments] = useState([]);

  const load = async () => {
    const [t, c, b, co] = await Promise.all([
      api.get("/transactions"),
      api.get("/categories"),
      api.get("/bank-accounts"),
      api.get("/contacts"),
    ]);
    setItems(t.data); setCats(c.data); setBanks(b.data); setContacts(co.data);
  };
  useEffect(() => { load(); }, []);

  const filtered = items.filter((i) => filter === "all" ? true : i.type === filter);

  const openNew = () => { setEditing(null); setForm(emptyForm); setAttachments([]); setOpen(true); };
  const openEdit = (t) => {
    setEditing(t);
    setForm({
      type: t.type, amount: t.amount, currency: t.currency, date: t.date,
      category_id: t.category_id || "", bank_account_id: t.bank_account_id || "",
      contact_id: t.contact_id || "", description: t.description || "",
      attachment_ids: t.attachment_ids || [],
    });
    setAttachments([]);
    setOpen(true);
  };

  const save = async () => {
    const payload = {
      ...form,
      amount: parseFloat(form.amount),
      category_id: form.category_id || null,
      bank_account_id: form.bank_account_id || null,
      contact_id: form.contact_id || null,
    };
    if (!payload.amount || isNaN(payload.amount)) {
      toast.error("Geçerli bir tutar girin"); return;
    }
    try {
      if (editing) await api.put(`/transactions/${editing.id}`, payload);
      else await api.post("/transactions", payload);
      toast.success(editing ? "İşlem güncellendi" : "İşlem eklendi");
      setOpen(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Bu işlemi silmek istiyor musunuz?")) return;
    await api.delete(`/transactions/${id}`);
    toast.success("Silindi");
    await load();
  };

  const uploadFile = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/attachments/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAttachments((a) => [...a, data]);
      setForm((f) => ({ ...f, attachment_ids: [...(f.attachment_ids || []), data.id] }));
      toast.success("Dosya yüklendi");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setUploading(false); }
  };

  const catName = (id) => cats.find((c) => c.id === id)?.name || "-";
  const bankName = (id) => banks.find((b) => b.id === id)?.name || "-";
  const contactName = (id) => contacts.find((c) => c.id === id)?.name || "-";

  return (
    <div className="space-y-6 slide-up" data-testid="transactions-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-indigo-400 mb-2">İşlemler</div>
          <h1 className="text-4xl font-bold" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Gelir ve Giderler</h1>
          <p className="text-[#94A3B8] mt-1 text-sm">Tüm nakit hareketlerini kaydedin ve kategorize edin.</p>
        </div>
        <div className="flex items-center gap-3">
          <Tabs value={filter} onValueChange={setFilter}>
            <TabsList className="bg-[#131D31] border border-[#1E293B]">
              <TabsTrigger value="all" data-testid="tab-all">Tümü</TabsTrigger>
              <TabsTrigger value="income" data-testid="tab-income">Gelir</TabsTrigger>
              <TabsTrigger value="expense" data-testid="tab-expense">Gider</TabsTrigger>
            </TabsList>
          </Tabs>
          <Button data-testid="new-transaction-btn" onClick={openNew} className="bg-indigo-500 hover:bg-indigo-600">
            <Plus className="w-4 h-4 mr-2" /> Yeni İşlem
          </Button>
        </div>
      </div>

      <div className="card-elev overflow-hidden" data-testid="transactions-list">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#0F172A] text-[#94A3B8]">
              <tr>
                <th className="text-left px-5 py-3 font-medium">Tarih</th>
                <th className="text-left px-5 py-3 font-medium">Açıklama</th>
                <th className="text-left px-5 py-3 font-medium">Kategori</th>
                <th className="text-left px-5 py-3 font-medium">Banka</th>
                <th className="text-left px-5 py-3 font-medium">Cari</th>
                <th className="text-right px-5 py-3 font-medium">Tutar</th>
                <th className="text-right px-5 py-3 font-medium">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={7} className="text-center py-12 text-[#94A3B8]">Kayıt bulunamadı. Yeni işlem ekleyin.</td></tr>
              )}
              {filtered.map((t) => (
                <tr key={t.id} className="border-t border-[#1E293B] hover:bg-[#0F172A]/50" data-testid={`transaction-row-${t.id}`}>
                  <td className="px-5 py-3 text-[#94A3B8]">{formatDate(t.date)}</td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${t.type === "income" ? "bg-emerald-500/15 text-emerald-400" : "bg-red-500/15 text-red-400"}`}>
                        {t.type === "income" ? <ArrowUpRight className="w-3.5 h-3.5"/> : <ArrowDownRight className="w-3.5 h-3.5"/>}
                      </div>
                      <span className="truncate max-w-[260px]">{t.description || "—"}</span>
                    </div>
                  </td>
                  <td className="px-5 py-3 text-[#94A3B8]">{catName(t.category_id)}</td>
                  <td className="px-5 py-3 text-[#94A3B8]">{bankName(t.bank_account_id)}</td>
                  <td className="px-5 py-3 text-[#94A3B8]">{contactName(t.contact_id)}</td>
                  <td className={`px-5 py-3 text-right mono font-semibold ${t.type === "income" ? "text-emerald-400" : "text-red-400"}`}>
                    {t.type === "income" ? "+" : "−"}{formatMoney(t.amount, t.currency)}
                  </td>
                  <td className="px-5 py-3 text-right">
                    <button data-testid={`edit-tx-${t.id}`} onClick={() => openEdit(t)} className="p-1.5 rounded-lg text-[#94A3B8] hover:text-indigo-400 hover:bg-indigo-500/10 mr-1"><Pencil className="w-3.5 h-3.5"/></button>
                    <button data-testid={`delete-tx-${t.id}`} onClick={() => remove(t.id)} className="p-1.5 rounded-lg text-[#94A3B8] hover:text-red-400 hover:bg-red-500/10"><Trash2 className="w-3.5 h-3.5"/></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#131D31] border-[#1E293B] text-white max-w-xl" data-testid="transaction-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "İşlemi Düzenle" : "Yeni İşlem"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-[#94A3B8] mb-1 block">Tür</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="tx-type" className="bg-[#0F172A] border-[#1E293B]"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#131D31] border-[#1E293B]">
                    <SelectItem value="income">Gelir</SelectItem>
                    <SelectItem value="expense">Gider</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-[#94A3B8] mb-1 block">Tarih</Label>
                <Input type="date" data-testid="tx-date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="bg-[#0F172A] border-[#1E293B]" />
              </div>
              <div>
                <Label className="text-xs text-[#94A3B8] mb-1 block">Tutar</Label>
                <Input type="number" step="0.01" data-testid="tx-amount" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="bg-[#0F172A] border-[#1E293B]" placeholder="0,00" />
              </div>
              <div>
                <Label className="text-xs text-[#94A3B8] mb-1 block">Para Birimi</Label>
                <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                  <SelectTrigger data-testid="tx-currency" className="bg-[#0F172A] border-[#1E293B]"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#131D31] border-[#1E293B]">
                    <SelectItem value="TRY">TRY ₺</SelectItem>
                    <SelectItem value="USD">USD $</SelectItem>
                    <SelectItem value="EUR">EUR €</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-[#94A3B8] mb-1 block">Kategori</Label>
                <Select value={form.category_id || "none"} onValueChange={(v) => setForm({ ...form, category_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="tx-category" className="bg-[#0F172A] border-[#1E293B]"><SelectValue placeholder="Seç"/></SelectTrigger>
                  <SelectContent className="bg-[#131D31] border-[#1E293B]">
                    <SelectItem value="none">—</SelectItem>
                    {cats.filter((c) => c.type === form.type).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-[#94A3B8] mb-1 block">Banka Hesabı</Label>
                <Select value={form.bank_account_id || "none"} onValueChange={(v) => setForm({ ...form, bank_account_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="tx-bank" className="bg-[#0F172A] border-[#1E293B]"><SelectValue placeholder="Seç"/></SelectTrigger>
                  <SelectContent className="bg-[#131D31] border-[#1E293B]">
                    <SelectItem value="none">—</SelectItem>
                    {banks.map((b) => <SelectItem key={b.id} value={b.id}>{b.name} ({b.currency})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label className="text-xs text-[#94A3B8] mb-1 block">Cari Hesap (opsiyonel)</Label>
                <Select value={form.contact_id || "none"} onValueChange={(v) => setForm({ ...form, contact_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="tx-contact" className="bg-[#0F172A] border-[#1E293B]"><SelectValue placeholder="Seç"/></SelectTrigger>
                  <SelectContent className="bg-[#131D31] border-[#1E293B]">
                    <SelectItem value="none">—</SelectItem>
                    {contacts.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} ({c.type === "customer" ? "Müşteri" : "Tedarikçi"})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label className="text-xs text-[#94A3B8] mb-1 block">Açıklama</Label>
                <Textarea data-testid="tx-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-[#0F172A] border-[#1E293B] min-h-[70px]" placeholder="Örn: Nisan ayı elektrik faturası" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs text-[#94A3B8] mb-1 block">Fatura / Belge (opsiyonel)</Label>
                <label className="flex items-center gap-2 px-3 py-2.5 rounded-lg border border-dashed border-[#1E293B] hover:border-indigo-500/50 cursor-pointer bg-[#0F172A] text-sm text-[#94A3B8]">
                  <Paperclip className="w-4 h-4" />
                  <span>{uploading ? "Yükleniyor…" : "Dosya seç veya sürükle"}</span>
                  <input type="file" className="hidden" data-testid="tx-file-input" onChange={(e) => e.target.files?.[0] && uploadFile(e.target.files[0])} />
                </label>
                {attachments.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {attachments.map((a) => (
                      <div key={a.id} className="text-xs text-indigo-400 flex items-center gap-2">
                        <Paperclip className="w-3 h-3" /> {a.original_filename}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} data-testid="tx-cancel-btn">İptal</Button>
            <Button onClick={save} data-testid="tx-save-btn" className="bg-indigo-500 hover:bg-indigo-600">{editing ? "Güncelle" : "Kaydet"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
